/* eslint-disable max-len */
/* eslint-disable no-console */
import { API, graphqlOperation } from 'aws-amplify';
import {
	CREATE_CATEGORY,
	UPDATE_CATEGORY,
	DELETE_CATEGORY,
	GET_CATEGORIES,
	CategoryPayload,
	Category,
	CategoryResponse,
	AddCategoryContentPayload,
	UpdateContentOrderPayload
} from './types';
import { toast } from 'react-toastify';

// API endpoint name as configured in index.tsx
const API_NAME = 'revisedResources';

// GraphQL query definitions
const GET_CATEGORY_BY_ID = `
query GetCategory($id: ID!, $from: String) {
  category(id: $id, from: $from) {
    success
    message
    data {
      _id
      name
      description
      thumbnailUrl
      companyId
      isEnabled
      visibility
	  allowShare
	  allowDownloads
	  accessControl {
        internalEmployees {
          allSelected
          departments {
            name
            count
          }
          specificUsers {
            userId
            email
            name
            role
          }
        }
        userGroups {
          type
          count
        }
        participantCount
        participantLimit
      }
      contentCount {
        total
        videos
        articles
        podcasts
        documents
        images
      }
    }
  }
}`;

const GET_CATEGORIES_QUERY = `
query GetCategories($companyId: ID!, $page: Int, $limit: Int, $from: String, $filters: CategoryFilters, $sort: CategorySortInput) {
  categories(companyId: $companyId, page: $page, limit: $limit, from: $from, filters: $filters, sort: $sort) {
    success
    message
    data {
      _id
      name
      description
      thumbnailUrl
	  thumbnailSource
	  companyId
      isEnabled
	  allowShare
	  allowDownloads
      visibility
      parentCategoryId
      isSubcategory
      level
      subcategoryCount
      accessControl {
        internalEmployees {
          allSelected
          departments {
            name
            count
          }
          specificUsers {
            userId
            email
            name
            role
          }
        }
        userGroups {
          type
          count
        }
        participantCount
        participantLimit
      }
      contentCount {
        total
        videos
        articles
        podcasts
        documents
        images
      }
    }
    pagination {
      total
      page
      limit
      pages
    }
  }
}`;

const GET_SUBCATEGORIES_QUERY = `
query GetSubcategories($parentCategoryId: ID!, $page: Int, $limit: Int, $from: String, $filters: CategoryFilterInput, $sort: CategorySortInput) {
  subcategories(parentCategoryId: $parentCategoryId, page: $page, limit: $limit, from: $from, filters: $filters, sort: $sort) {
    success
    message
    data {
      _id
      name
      description
      thumbnailUrl
      companyId
      isEnabled
      visibility
	  allowShare
	  allowDownloads
      parentCategoryId
      isSubcategory
      level
      subcategoryCount
      accessControl {
        internalEmployees {
          allSelected
          departments {
            name
            count
          }
          specificUsers {
            userId
            email
            name
            role
          }
        }
        userGroups {
          type
          count
        }
        participantCount
        participantLimit
      }
      contentCount {
        total
        videos
        articles
        podcasts
        documents
        images
      }
    }
    pagination {
      total
      page
      limit
      pages
    }
  }
}`;
// const GET_CATEGORY_CONTENTS_QUERY = 'query GetCategoryContents($categoryId: ID!, $contentType: ContentType, $page: Int, $limit: Int, $sort: CategorySortInput) { categoryContents(categoryId: $categoryId, contentType: $contentType, page: $page, limit: $limit, sort: $sort) { success message data { _id name contents { contentId contentType addedAt order addedBy { userId name } } } pagination { total page limit pages } } }';
const GET_CATEGORY_CONTENTS_QUERY =
`query
GetCategoryContents($categoryId: ID!, $contentType: ContentType, $page: Int, $limit: Int, $from: String)
{ categoryContents(categoryId: $categoryId, contentType: $contentType, page: $page, limit: $limit, from: $from)
{ success message data { _id name contents { contentId contentType addedAt order addedBy { userId name } } } pagination { total page limit pages } } }`;

/**
 * Create a new category or subcategory
 * @param payload - Category data to create
 * @returns The created category data
 */
export const createCategory = async (
	payload: CategoryPayload
): Promise<Category> =>
{
	if (!payload.name || !payload.name.trim())
	{
		console.error('Category name is required for createCategory');
		throw new Error('Category name is required');
	}

	if (!payload.companyId)
	{
		console.error('Company ID is required for createCategory');
		throw new Error('Company ID is required');
	}

	try
	{
		const response = await API.post(
			API_NAME,
			'/categories',
			{
				body : payload
			}
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from createCategory:', response);
		throw new Error(response?.message || 'Failed to create category');
	}
	catch (error)
	{
		console.error('Error creating category:', error);
		throw error;
	}
};

/**
 * Get categories for a company
 * @param companyId - Company identifier
 * @param options - Query options (page, limit, filters)
 * @returns List of categories and pagination info
 */
export const getCategories = async (
	companyId: string,
	options: {
    page?: number;
    limit?: number;
    parentCategoryId?: string | 'null';
    level?: number;
    isSubcategory?: boolean;
    contentType?: string;
    isEnabled?: boolean;
	from: 'MyResource' | 'Configuration';
  } = { from: 'MyResource' }
): Promise<{ categories: Category[]; pagination: any }> =>
{
	if (!companyId)
	{
		console.error('Company ID is required for getCategories');
		throw new Error('Company ID is required');
	}

	try
	{
		// Construct query parameters
		const queryStringParameters: Record<string, any> = { ...options };

		// Remove parentCategoryId if it's the string 'null' (for root categories)
		// The API expects no parentCategoryId parameter for root categories, not the string 'null'
		if (queryStringParameters.parentCategoryId === 'null')
		{
			delete queryStringParameters.parentCategoryId;
		}

		const response = await API.get(API_NAME, `/categories/company/${companyId}`, { queryStringParameters }) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return {
				categories : response.data as Category[],
				pagination : response.pagination || { total: 0, page: 1, limit: 50, pages: 0 }
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from getCategories:', response);

		return { categories: [], pagination: { total: 0, page: 1, limit: 50, pages: 0 } };
	}
	catch (error)
	{
		console.error('Error getting categories:', error);
		throw error;
	}
};

/**
 * Get a specific category by ID
 * @param categoryId - Category identifier
 * @returns The category data
 */
export const getCategoryById = async (
	categoryId: string,
	queryStringParameters?: Record<string, any>
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for getCategoryById');
		throw new Error('Category ID is required');
	}

	try
	{
		const response = await API.get(
			API_NAME,
			`/categories/${categoryId}`,
			{ queryStringParameters }
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from getCategoryById:', response);
		throw new Error(response?.message || 'Failed to get category');
	}
	catch (error:any)
	{
		console.error('Error getting category by ID:', error, error?.response?.data, error?.response?.data?.message);

		if (error?.response?.data?.message)
		{
			toast.error(error?.response?.data?.message, {
				position  : toast.POSITION.TOP_RIGHT,
				className : 'toast-message'
			});
		}
		throw error;
	}
};

/**
 * Get subcategories of a specific category
 * @param categoryId - Parent category identifier
 * @returns List of subcategories
 */
export const getSubcategories = async (
	categoryId: string,
	queryStringParameters?: Record<string, any>
): Promise<Category[]> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for getSubcategories');
		throw new Error('Category ID is required');
	}

	try
	{
		const response = await API.get(
			API_NAME,
			`/categories/${categoryId}/subcategories`,
			{ queryStringParameters }
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category[];
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from getSubcategories:', response);

		return [];
	}
	catch (error)
	{
		console.error('Error getting subcategories:', error);
		throw error;
	}
};

/**
 * Update a category (full replacement)
 * @param categoryId - Category identifier
 * @param payload - Updated category data
 * @returns The updated category
 */
export const updateCategory = async (
	categoryId: string,
	payload: Partial<CategoryPayload>
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for updateCategory');
		throw new Error('Category ID is required');
	}

	try
	{
		const response = await API.put(
			API_NAME,
			`/categories/${categoryId}`,
			{
				body : payload
			}
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from updateCategory:', response);
		throw new Error(response?.message || 'Failed to update category');
	}
	catch (error)
	{
		console.error('Error updating category:', error);
		throw error;
	}
};

/**
 * Update a category's visibility
 * @param categoryId - Category identifier
 * @param visibility - New visibility status ('public' or 'private')
 * @returns The updated category
 */
export const updateCategoryVisibility = async (
	categoryId: string,
	visibility: 'public' | 'private'
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for updateCategoryVisibility');
		throw new Error('Category ID is required');
	}

	try
	{
		const response = await API.patch(
			API_NAME,
			`/categories/${categoryId}/visibility`,
			{
				body : { visibility }
			}
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from updateCategoryVisibility:', response);
		throw new Error(response?.message || 'Failed to update category visibility');
	}
	catch (error)
	{
		console.error('Error updating category visibility:', error);
		throw error;
	}
};

/**
 * Delete a category
 * @param categoryId - Category identifier
 * @returns Success status
 */
export const deleteCategory = async (
	categoryId: string
): Promise<boolean> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for deleteCategory');
		throw new Error('Category ID is required');
	}

	try
	{
		await API.del(
			API_NAME,
			`/categories/${categoryId}`,
			{}
		);

		// API returns 204 No Content on success, which might be parsed as null or undefined
		return true;
	}
	catch (error)
	{
		console.error('Error deleting category:', error);
		throw error;
	}
};

/**
 * Add content to a category
 * @param categoryId - Category identifier
 * @param payload - Content data to add
 * @returns The updated category with content added
 */
export const addContentToCategory = async (
	categoryId: string,
	payload: AddCategoryContentPayload
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for addContentToCategory');
		throw new Error('Category ID is required');
	}

	if (!payload.contentId)
	{
		console.error('Content ID is required for addContentToCategory');
		throw new Error('Content ID is required');
	}

	try
	{
		const response = await API.post(
			API_NAME,
			`/categories/${categoryId}/content`,
			{
				body : payload
			}
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from addContentToCategory:', response);
		throw new Error(response?.message || 'Failed to add content to category');
	}
	catch (error)
	{
		console.error('Error adding content to category:', error);
		throw error;
	}
};

/**
 * Remove content from a category
 * @param categoryId - Category identifier
 * @param contentId - Content identifier to remove
 * @returns The updated category with content removed
 */
export const removeContentFromCategory = async (
	categoryId: string,
	contentId: string
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for removeContentFromCategory');
		throw new Error('Category ID is required');
	}

	if (!contentId)
	{
		console.error('Content ID is required for removeContentFromCategory');
		throw new Error('Content ID is required');
	}

	try
	{
		const response = await API.del(
			API_NAME,
			`/categories/${categoryId}/content/${contentId}`,
			{}
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from removeContentFromCategory:', response);
		throw new Error(response?.message || 'Failed to remove content from category');
	}
	catch (error)
	{
		console.error('Error removing content from category:', error);
		throw error;
	}
};

/**
 * Update the order of content within a category
 * @param categoryId - Category identifier
 * @param payload - Content ordering data
 * @returns The updated category with new content order
 */
export const updateCategoryContentOrder = async (
	categoryId: string,
	payload: UpdateContentOrderPayload
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for updateCategoryContentOrder');
		throw new Error('Category ID is required');
	}

	if (!payload.contentOrders || payload.contentOrders.length === 0)
	{
		console.error('Content orders are required for updateCategoryContentOrder');
		throw new Error('Content orders are required');
	}

	try
	{
		const response = await API.patch(
			API_NAME,
			`/categories/${categoryId}/content-order`,
			{
				body : payload
			}
		) as CategoryResponse;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return response.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from updateCategoryContentOrder:', response);
		throw new Error(response?.message || 'Failed to update content order');
	}
	catch (error)
	{
		console.error('Error updating content order:', error);
		throw error;
	}
};

/**
 * Get a specific category by ID using GraphQL
 * @param categoryId - Category identifier
 * @returns The category data
 */
export const graphqlGetCategoryById = async (
	categoryId: string,
	from?: 'MyResource' | 'Configuration'
): Promise<Category> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for graphqlGetCategoryById');
		throw new Error('Category ID is required');
	}

	try
	{
		// const response = await API.graphql({
		// 	query     : GET_CATEGORY_BY_ID,
		// 	variables : { id: categoryId },
		// 	authMode  : 'AMAZON_COGNITO_USER_POOLS'
		// });

		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : { query: GET_CATEGORY_BY_ID, variables: { id: categoryId, from } }
		});

		// Extract the data from the GraphQL response
		const result = (response as any).data.category;

		// Check if the operation was successful
		if (result?.success && result?.data)
		{
			return result.data as Category;
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from graphqlGetCategoryById:', result);
		throw new Error(result?.message || 'Failed to get category');
	}
	catch (error)
	{
		console.error('Error getting category by ID with GraphQL:', error);
		throw error;
	}
};

/**
 * Get categories for a company using GraphQL
 * @param companyId - Company identifier
 * @param options - Query options (page, limit, filters, sort)
 * @returns List of categories and pagination info
 */
export const graphqlGetCategories = async (
	companyId: string,
	options: {
		page?: number;
		limit?: number;
		from?: 'MyResource' | 'Configuration';
		filters?: {
			isEnabled?: boolean;
			visibility?: 'PUBLIC' | 'PRIVATE';
			contentType?: string;
			timeRange?: { startDate: number; endDate: number };
		};
		sort?: {
			field: 'NAME' | 'CREATED_AT' | 'UPDATED_AT' | 'VIEW_COUNT';
			direction: 'ASC' | 'DESC';
		};
	} = {}
): Promise<{ categories: Category[]; pagination: any }> =>
{
	if (!companyId)
	{
		console.error('Company ID is required for graphqlGetCategories');
		throw new Error('Company ID is required');
	}

	try
	{
		// Prepare variables for GraphQL query
		const variables = {
			companyId,
			page    : options.page || 1,
			limit   : options.limit || 10,
			from    : options.from || undefined,
			filters : options.filters || undefined,
			sort    : options.sort || undefined
		};

		// const response = await API.graphql({
		// 	query : GET_CATEGORIES_QUERY,
		// 	variables
		// 	// authMode : 'AMAZON_COGNITO_USER_POOLS'
		// });

		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : { query: GET_CATEGORIES_QUERY, variables }
		});

		// Extract the data from the GraphQL response
		const result = (response as any).data.categories;

		// Check if the operation was successful
		if (result?.success && result?.data)
		{
			return {
				categories : result.data as Category[],
				pagination : result.pagination || { total: 0, page: 1, limit: 10, pages: 0 }
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from graphqlGetCategories:', result);

		return { categories: [], pagination: { total: 0, page: 1, limit: 10, pages: 0 } };
	}
	catch (error)
	{
		console.error('Error getting categories with GraphQL:', error);
		throw error;
	}
};

/**
 * Get subcategories of a specific category using GraphQL
 * @param parentCategoryId - Parent category identifier
 * @param options - Query options (page, limit, filters, sort)
 * @returns List of subcategories and pagination info
 */
export const graphqlGetSubcategories = async (
	parentCategoryId: string,
	options: {
		page?: number;
		limit?: number;
		from?: 'MyResource' | 'Configuration';
		filters?: {
			isEnabled?: boolean;
			visibility?: 'PUBLIC' | 'PRIVATE';
			contentType?: string;
		};
		sort?: {
			field: 'NAME' | 'CREATED_AT' | 'UPDATED_AT' | 'VIEW_COUNT';
			direction: 'ASC' | 'DESC';
		};
	} = {}
): Promise<{ subcategories: Category[]; pagination: any }> =>
{
	if (!parentCategoryId)
	{
		console.error('Parent category ID is required for graphqlGetSubcategories');
		throw new Error('Parent category ID is required');
	}

	try
	{
		// Prepare variables for GraphQL query
		const variables = {
			parentCategoryId,
			page    : options.page || 1,
			limit   : options.limit || 10,
			from    : options.from || undefined,
			filters : options.filters || undefined,
			sort    : options.sort || undefined
		};

		// const response = await API.graphql({
		// 	query    : GET_SUBCATEGORIES_QUERY,
		// 	variables,
		// 	authMode : 'AMAZON_COGNITO_USER_POOLS'
		// });

		const response = await API.graphql({
			query : GET_SUBCATEGORIES_QUERY,
			variables
		});

		// Extract the data from the GraphQL response
		const result = (response as any).data.subcategories;

		// Check if the operation was successful
		if (result?.success && result?.data)
		{
			return {
				subcategories : result.data as Category[],
				pagination    : result.pagination || { total: 0, page: 1, limit: 10, pages: 0 }
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from graphqlGetSubcategories:', result);

		return { subcategories: [], pagination: { total: 0, page: 1, limit: 10, pages: 0 } };
	}
	catch (error)
	{
		console.error('Error getting subcategories with GraphQL:', error);
		throw error;
	}
};

/**
 * Get content items within a specific category using GraphQL
 * @param categoryId - Category identifier
 * @param options - Query options (contentType, page, limit, sort)
 * @returns The category with content items and pagination info
 */
export const graphqlGetCategoryContents = async (
	categoryId: string,
	options: {
		contentType?: 'VIDEO' | 'PODCAST' | 'DOCUMENT' | 'ARTICLE' | 'IMAGE';
		page?: number;
		limit?: number;
		from?: 'MyResource' | 'Configuration';
		sort?: {
			field: 'CREATED_AT';
			direction: 'ASC' | 'DESC';
		};
	} = {}
): Promise<{ category: Category; contents: any[]; pagination: any }> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for graphqlGetCategoryContents');
		throw new Error('Category ID is required');
	}

	try
	{
		// Prepare variables for GraphQL query
		const variables = {
			categoryId,
			contentType : options.contentType || undefined,
			page        : options.page || 1,
			limit       : options.limit || 10,
			from        : options.from || undefined,
			sort        : options.sort || undefined
		};

		// Using graphqlOperation method instead of object notation
		const response = await API.graphql(
			graphqlOperation(GET_CATEGORY_CONTENTS_QUERY, variables)
			// To add auth mode, you can use a third parameter:
			// 'AMAZON_COGNITO_USER_POOLS'
		);

		// Extract the data from the GraphQL response
		const result = (response as any).data.categoryContents;

		// Check if the operation was successful
		if (result?.success && result?.data && result.data.length > 0)
		{
			const categoryWithContents = result.data[0];

			return {
				category : {
					_id  : categoryWithContents._id,
					name : categoryWithContents.name
				} as Category,
				contents   : categoryWithContents.contents || [],
				pagination : result.pagination || { total: 0, page: 1, limit: 10, pages: 0 }
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from graphqlGetCategoryContents:', result);

		return {
			category   : { _id: categoryId } as Category,
			contents   : [],
			pagination : { total: 0, page: 1, limit: 10, pages: 0 }
		};
	}
	catch (error)
	{
		console.error('Error getting category contents with GraphQL:', error);
		throw error;
	}
};

export const getCategoryContentsWithPut = async (
	categoryId: string,
	options: {
		contentType?: 'VIDEO' | 'PODCAST' | 'DOCUMENT' | 'ARTICLE' | 'IMAGE';
		page?: number;
		limit?: number;
		from?: 'MyResource' | 'Configuration';
		sort?: {
			field: 'CREATED_AT';
			direction: 'ASC' | 'DESC';
		};
	} = {}
): Promise<{ category: Category; contents: any[]; pagination: any }> =>
{
	if (!categoryId)
	{
		console.error('Category ID is required for getCategoryContentsWithPut');
		throw new Error('Category ID is required');
	}

	try
	{
		// Prepare payload for PUT request
		const payload = {
			contentType : options.contentType || undefined,
			page        : options.page || 1,
			limit       : options.limit || 10,
			from        : options.from || undefined,
			sort        : options.sort || undefined,
			categoryId
		};

		// Make PUT request - assuming API_NAME is 'revisedResources'
		const response = await API.post('resourceManagementGraphQL', '/', {
			body : { query: GET_CATEGORY_CONTENTS_QUERY, variables: payload }
		});

		// Extract the data from the response
		// Assuming response structure follows the REST API pattern
		if (response?.success && response?.data)
		{
			const categoryData = response.data;

			return {
				category : {
					_id  : categoryData._id || categoryId,
					name : categoryData.name || ''
				} as Category,
				contents   : categoryData.contents || [],
				pagination : response.pagination || { total: 0, page: 1, limit: 10, pages: 0 }
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from getCategoryContentsWithPut:', response);

		return {
			category   : { _id: categoryId } as Category,
			contents   : [],
			pagination : { total: 0, page: 1, limit: 10, pages: 0 }
		};
	}
	catch (error)
	{
		console.error('Error getting category contents with PUT:', error);

		return {
			category   : { } as Category,
			contents   : [],
			pagination : { total: 0, page: 1, limit: 10, pages: 0 }
		};
		// throw error;
	}
};

/**
 * Move all content from one category to another
 * @param sourceCategoryId - Source category identifier
 * @param targetCategoryId - Target category identifier
 * @returns The move-all operation result
 */
export const moveAllContent = async (
	sourceCategoryId: string,
	targetCategoryId: string
): Promise<Category> =>
{
	if (!sourceCategoryId)
	{
		console.error('Source category ID is required for moveAllContent');
		throw new Error('Source category ID is required');
	}

	if (!targetCategoryId)
	{
		console.error('Target category ID is required for moveAllContent');
		throw new Error('Target category ID is required');
	}

	try
	{
		const response = await API.post(
			API_NAME,
			`/categories/${sourceCategoryId}/move-all/${targetCategoryId}`,
			{
				// No body needed - moves all content automatically
			}
		) as any;

		// If the API call was successful and we got data
		if (response?.success && response?.updSourceCategory)
		{
			return response.updSourceCategory as Category;
			// return {
			// 	sourceCategory : response.data.sourceCategory as Category,
			// 	targetCategory : response.data.targetCategory as Category,
			// 	summary        : response.data.summary,
			// 	details        : response.data.details
			// };
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from moveAllContent:', response);
		throw new Error(response?.message || 'Failed to move all content');
	}
	catch (error)
	{
		console.error('Error moving all content:', error);
		throw error;
	}
};

/**
 * Transfer a specific content item from one category to another
 * @param sourceCategoryId - Source category identifier
 * @param targetCategoryId - Target category identifier
 * @param contentId - Content item identifier
 * @param contentType - Type of content (video, podcast, document, article, image, playlist)
 * @returns The transfer operation result
 *
 * @example
 * // Move a video from one category to another
 * const result = await transferContentItem(
 *   'sourceCategoryId123',
 *   'targetCategoryId456',
 *   'contentId789',
 *   'video'
 * );
 * console.log('Source category updated:', result.sourceCategory);
 * console.log('Target category updated:', result.targetCategory);
 */
export const transferContentItem = async (
	sourceCategoryId: string,
	targetCategoryId: string,
	contentId: string,
	contentType: string
): Promise<{
	sourceCategory: Category;
	targetCategory: Category;
}> =>
{
	if (!sourceCategoryId)
	{
		console.error('Source category ID is required for transferContentItem');
		throw new Error('Source category ID is required');
	}

	if (!targetCategoryId)
	{
		console.error('Target category ID is required for transferContentItem');
		throw new Error('Target category ID is required');
	}

	if (!contentId)
	{
		console.error('Content ID is required for transferContentItem');
		throw new Error('Content ID is required');
	}

	if (!contentType)
	{
		console.error('Content type is required for transferContentItem');
		throw new Error('Content type is required');
	}

	try
	{
		const response = await API.post(
			API_NAME,
			`/categories/${sourceCategoryId}/transfer/${targetCategoryId}`,
			{
				body : {
					contentId,
					contentType
				}
			}
		) as any;

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return {
				sourceCategory : response.data.sourceCategory as Category,
				targetCategory : response.data.targetCategory as Category
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from transferContentItem:', response);
		throw new Error(response?.message || 'Failed to transfer content item');
	}
	catch (error)
	{
		console.error('Error transferring content item:', error);
		throw error;
	}
};

/**
 * Find categories that contain a specific content item (reverse lookup)
 * @param contentId - Content identifier
 * @returns Categories containing the content item
 */
export const findCategoriesContaining = async (contentId: string): Promise<{ categories: Category[] }> =>
{
	if (!contentId)
	{
		console.error('Content ID is required for findCategoriesContaining');
		throw new Error('Content ID is required');
	}

	try
	{
		const response = await API.get(
			API_NAME,
			`/categories/content/${contentId}`,
			{}
		) as { success: boolean; data: Category[]; count: number };

		// If the API call was successful and we got data
		if (response?.success && response?.data)
		{
			return {
				categories : response.data as Category[]
			};
		}

		// If the response format is unexpected or API returned an error
		console.warn('Unexpected response format from findCategoriesContaining:', response);

		return { categories: [] };
	}
	catch (error)
	{
		console.error('Error finding categories containing content:', error);
		throw error;
	}
};